import { cabinClassNames } from "@/constants/flight/cabinClass"
import { TypeAirport } from "@/lib/redux/features/flight/type"
import { getTranslatedText } from "@/utils/safeParse/getTranslatedText"
import { formatCurrency, formatCurrencyUSD } from "@/utils/string/formatCurrency"
import dayjs, { Dayjs } from "dayjs"
import { useTranslation } from "react-i18next"

const commonCabinClassNames: Record<string, { en: string; vi: string }> = {
  ECONOMY: { en: "Economy", vi: "Phổ thông" },
  ECONOMYLITE: { en: "Economy Lite", vi: "Phổ thông tiết kiệm" },
  ECONOMYSUPERLITE: { en: "Economy Super Lite", vi: "Phổ thông siêu tiết kiệm" },
  ECONOMYCLASSIC: { en: "Economy Classic", vi: "Phổ thông tiêu chuẩn" },
  ECONOMYFLEX: { en: "Economy Flex", vi: "Phổ thông linh hoạt" },
  ECONOMYPLUS: { en: "Economy Plus", vi: "Phổ thông cao cấp" },
  ECONOMYSMART: { en: "Economy Smart", vi: "Phổ thông thông minh" },
  ECONOMYSAVER: { en: "Economy Saver", vi: "Phổ thông tiết kiệm" },
  ECONOMYSAVERMAX: { en: "Economy Saver Max", vi: "Phổ thông tiết kiệm tối đa" },
  PREMIUMECONOMY: { en: "Premium Economy", vi: "Phổ thông đặc biệt" },
  PREMIUMECONOMYPLUS: { en: "Premium Economy Plus", vi: "Phổ thông đặc biệt cao cấp" },
  PREMIUMECONOMYSTANDARD: { en: "Premium Economy Standard", vi: "Phổ thông đặc biệt tiêu chuẩn" },
  PREMIUMECONOMYCLASSIC: { en: "Premium Economy Classic", vi: "Phổ thông đặc biệt tiêu chuẩn" },
  PREMIUMECONOMYFLEX: { en: "Premium Economy Flex", vi: "Phổ thông đặc biệt linh hoạt" },
  BUSINESS: { en: "Business", vi: "Thương gia" },
  BUSINESSCLASSIC: { en: "Business Classic", vi: "Thương gia tiêu chuẩn" },
  BUSINESSFLEX: { en: "Business Flex", vi: "Thương gia linh hoạt" },
  BUSINESSSMART: { en: "Business Smart", vi: "Thương gia linh hoạt" },
  BUSINESSPRIME: { en: "Business Prime", vi: "Thương gia cao cấp" },
  BUSINESSELITE: { en: "Business Elite", vi: "Thương gia cao cấp" },
  FIRSTCLASS: { en: "First Class", vi: "Hạng nhất" },
  PREMIUMFLEX: { en: "Premium Flex", vi: "Phổ thông linh hoạt cao cấp" },
}

const normalizeCabinClassKey = (value: string) => value.replace(/[^a-z0-9]/gi, "").toUpperCase()

const formatCabinClassName = (value: string) =>
  value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")

export const useTETranslation = () => {
  const { i18n } = useTranslation()
  const getFormatCurrency = (value: any, short?: boolean) => {
    if (i18n.language === 'en' && typeof localStorage !== 'undefined') {
      const exchange = JSON.parse(localStorage.getItem('currency') || '[]')[0]
      const rate = parseFloat(exchange?.rate)
      if (!!exchange && Number.isFinite(rate) && rate > 0) {
        // rate = VND per 1 USD, so VND -> USD is a division
        return formatCurrencyUSD.format(value / rate)
      }
    }
    if (i18n.language !== 'en' && short) {
      return `${Math.floor(value) / 1000}K`
    }
    return formatCurrency.format(value)
  }

  const getValidDayjs = (value: any): Dayjs => {
    let dayjsValue: any = value
    
    if (dayjs.locale() !== i18n.language) {
      dayjs.locale(i18n.language)
    }

    if (!dayjs.isDayjs(dayjsValue)) {
      dayjsValue = dayjs(dayjsValue)
      if (!dayjsValue.isValid()) {
        dayjsValue = dayjs()
      }
    }
  
    return dayjsValue
  }

  return {
    getFormatCurrency,
    getValidDayjs,
    getTranslatedText: (translatedText?: string, text?: string) => {
      if (i18n.language === 'en') {
        return getTranslatedText(text, translatedText)
      }
      return getTranslatedText(translatedText, text)
    },
    getAirportCityName: (airport?: TypeAirport, { shownCode }: { shownCode?: boolean } = { shownCode: false }) => {
      if (!airport) {
        return ''
      }

      if (i18n.language === 'en') {
        return `${getTranslatedText(airport.cityEn, airport.cityVi)}${!shownCode ? '' : ` (${airport?.code})`}`
      }
      return `${getTranslatedText(airport.cityVi, airport.cityEn)}${!shownCode ? '' : ` (${airport?.code})`}`
    },
    getAirportName: (airport?: TypeAirport, { shownCode }: { shownCode?: boolean } = { shownCode: false }) => {
      if (!airport) {
        return ''
      }
      if (i18n.language === 'en') {
        return `${getTranslatedText(airport.nameEn, airport.nameVi) || airport?.country || ''}${!shownCode ? '' : ` (${airport?.code})`}`
      }
      return `${getTranslatedText(airport.nameVi, airport.nameEn) || airport?.country || ''}${!shownCode ? '' : ` (${airport?.code})`}`
    },
    getCabinClassName: (airline: string, cabinClass: string) => {
      const rawCabinClass = String(cabinClass || "").trim()
      if (!rawCabinClass) return ""
      const airlineCode = airline.replace(/^_/, "").toUpperCase()
      const lookupCabinClass = airlineCode === "AK" ? rawCabinClass[0] : rawCabinClass
      const airlineCabinNames =
        (cabinClassNames as any)[airline] ||
        (cabinClassNames as any)[airlineCode] ||
        (cabinClassNames as any)[`_${airlineCode}`] ||
        {}
      const mappedCabinClass = Object.entries(airlineCabinNames).find(
        ([key]) => normalizeCabinClassKey(key) === normalizeCabinClassKey(lookupCabinClass),
      )?.[1] as string | undefined
      const rawCommonCabinClass = commonCabinClassNames[normalizeCabinClassKey(rawCabinClass)]
      const mappedCommonCabinClass = mappedCabinClass
        ? commonCabinClassNames[normalizeCabinClassKey(mappedCabinClass)]
        : undefined
      const commonCabinClass = airlineCode === "AK"
        ? mappedCommonCabinClass || rawCommonCabinClass
        : rawCommonCabinClass || mappedCommonCabinClass

      if (i18n.language === "en") {
        return commonCabinClass?.en || formatCabinClassName(mappedCabinClass || rawCabinClass)
      }

      return (
        commonCabinClass?.vi ||
        mappedCabinClass ||
        formatCabinClassName(rawCabinClass)
      )
    }
  }
}